// Single source of truth for inonit event categories.
//
// Every consumer (HomePage, FeedPage, CreateEventPage, EditEventPage,
// EventCard, MapPage, AdminEvents, defaultEventImages) reads from here so
// that adding/removing a category is a one-line change.
//
// Each entry includes:
//   key   — canonical title-case label persisted in events.category
//   emoji — lightweight chip glyph (consistent with existing aesthetic)
//   tone  — short word for filter pill copy ("vibe")
//   color — accent token used by chips/badges in the chunky/lime palette.
//            Stored as tailwind-friendly inline-style hex so the JIT doesn't
//            need every shade pre-registered. INK + NEON stay reserved for
//            the brand chrome; categories use desaturated mood tints.
//   fallbackImage — public path to the default cover when host hasn't
//            uploaded one. Lives in /public/default-events/.

const FALLBACK = "/default-events";

export const CATEGORIES = [
  { key: "Outdoors",   emoji: "🏞️", tone: "outdoor",      color: "#7BA46C", fallbackImage: `${FALLBACK}/outdoors.png` },
  { key: "Food",       emoji: "🍴",  tone: "tastes",       color: "#D17A47", fallbackImage: `${FALLBACK}/food.png` },
  { key: "Music",      emoji: "🎧",  tone: "sound",        color: "#9C6BC8", fallbackImage: `${FALLBACK}/music.png` },
  { key: "Arts",       emoji: "🎨",  tone: "create",       color: "#C8736B", fallbackImage: `${FALLBACK}/arts.png` },
  { key: "Books",      emoji: "📖",  tone: "read",         color: "#9C8B6E", fallbackImage: `${FALLBACK}/books.png` },
  { key: "Fitness",    emoji: "💪",  tone: "motion",       color: "#6CB28E", fallbackImage: `${FALLBACK}/fitness.png` },
  { key: "Networking", emoji: "🤝",  tone: "connect",      color: "#5A7FB2", fallbackImage: `${FALLBACK}/networking.png` },
  { key: "Gaming",     emoji: "🎮",  tone: "play",         color: "#C462C8", fallbackImage: `${FALLBACK}/gaming.png` },
  { key: "Movies",     emoji: "🎬",  tone: "cinema",       color: "#B85C7A", fallbackImage: `${FALLBACK}/movies.png` },
  { key: "Nightlife",  emoji: "🌃",  tone: "after dark",   color: "#7B5CC8", fallbackImage: `${FALLBACK}/nightlife.png` },
  { key: "Other",      emoji: "✦",   tone: "everything",   color: "#8B7E69", fallbackImage: `${FALLBACK}/other.png` },
];

export const CATEGORY_KEYS = CATEGORIES.map((c) => c.key);

// Quick lookup maps for hot paths (EventCard / MapPage marker render).
export const CATEGORY_BY_KEY = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c]),
);
export const CATEGORY_BY_LOWER = Object.fromEntries(
  CATEGORIES.map((c) => [c.key.toLowerCase(), c]),
);

/**
 * Lookup a category by any reasonable form (Title, lowercase, trimmed).
 * Returns the `Other` entry as a safe fallback for backward compatibility
 * with legacy events stored with arbitrary strings (e.g. "Art" without s,
 * deleted custom categories, etc.).
 */
export function resolveCategory(raw) {
  if (!raw) return CATEGORY_BY_KEY.Other;
  const k = String(raw).trim();
  return (
    CATEGORY_BY_KEY[k] ||
    CATEGORY_BY_LOWER[k.toLowerCase()] ||
    CATEGORY_BY_KEY.Other
  );
}

export const CATEGORY_EMOJI = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c.emoji]),
);
